/**
 * Industry rate-card intake API (contracts-plan.md Appendix A "Industry",
 * §13.5 WP-44; §7.7, §5.7 cta_industry_datapoints).
 *
 * Same house rule as api/rateCard.ts and api/intake.ts: never import axios
 * directly, go through services/api. This file is a SEPARATE module (not an
 * addition to client.ts, which other WPs append to concurrently) — see
 * ../../../../backend/src/routes/contracts/industry.mount.md's "Frontend
 * re-export" section for the one-line re-export the orchestrator applies at
 * the MOUNT step.
 */
import api from '../../services/api';

/** A stored cta_industry_datapoints row (§5.7). */
export interface IndustryDatapoint {
  id: string;
  network_id: string;
  entry_id: string;
  value: number;
  source_name: string;
  source_date: string;
  site_descriptor: string | null;
  region: string | null;
  therapeutic_area_id: string | null;
  uploaded_by: string;
  provenance: Record<string, unknown>;
}

/** One ranked candidate a person chooses from (masterLists/match.js rankCandidates, §7.5). */
export interface IndustryMatchCandidate {
  entry: { id: string; official_name: string; list_type: string; section: string | null };
  score: number;
  matchedOn: string;
  matchLabel: string;
  reasons: Array<{ code: string; detail: string; weight: number }>;
}

/**
 * A row the upload could not store as-is (§7.7, G11 — a person matches it
 * through the master-list decide route and re-uploads). `reason` is present
 * only for a row a person cannot fix by matching alone; its absence means
 * the label and value were both fine and this is a genuine §7.5 matching
 * decision.
 */
export interface UnmatchedIndustryRow {
  row: number;
  label: string;
  value: number | string | null;
  candidates: IndustryMatchCandidate[];
  reason?: 'missing_label' | 'missing_value' | 'non_numeric_value' | 'negative_value' | 'percentage_not_a_fraction';
}

export interface UploadIndustryResult {
  stored: IndustryDatapoint[];
  unmatched: UnmatchedIndustryRow[];
}

export interface UploadIndustryInput {
  sourceName: string;
  /** ISO date (YYYY-MM-DD). */
  sourceDate: string;
  siteDescriptor?: string;
  region?: string;
  therapeuticAreaId?: string;
}

/** POST /industry/upload — multipart form, field 'file' (§7.7). */
export async function uploadIndustryFile(file: File, input: UploadIndustryInput): Promise<UploadIndustryResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('source_name', input.sourceName);
  form.append('source_date', input.sourceDate);
  if (input.siteDescriptor) form.append('site_descriptor', input.siteDescriptor);
  if (input.region) form.append('region', input.region);
  if (input.therapeuticAreaId) form.append('therapeutic_area_id', input.therapeuticAreaId);

  const response = await api.post<UploadIndustryResult>('/api/contracts/industry/upload', form);
  return response.data;
}

/** GET /industry?entryId's exact response shape (§7.7: the two ranges side by side). */
export interface IndustryRangeResult {
  entry_id: string;
  wcr: { low: number | null; preferred: number | null; high: number | null; count: number };
  industry: {
    low: number | null;
    high: number | null;
    count: number;
    latest: { value: number; source_name: string; source_date: string } | null;
    datapoints: IndustryDatapoint[];
  };
}

/** GET /industry?entryId — the industry range beside the WCR range (§7.7). entryId is required. */
export async function getIndustryRange(entryId: string): Promise<IndustryRangeResult> {
  const response = await api.get<IndustryRangeResult>('/api/contracts/industry', { params: { entryId } });
  return response.data;
}
